import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { DealCardConfigProvider } from "@/providers/DealCardConfigProvider";
import { GlobalThemeProvider } from "@/providers/GlobalThemeProvider";
import { RootErrorBoundary } from "@/components/RootErrorBoundary";
import { initMerchantIndex } from "@/lib/merchantIndex";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import SpendEarn from "./pages/SpendEarn";
// CR042: Admin pages
import PromptPacks from "./pages/admin/PromptPacks";
import PromptsLibrary from "./pages/admin/PromptsLibrary";
import AIProviders from "./pages/admin/AIProviders";
import ScanConfiguration from "./pages/admin/ScanConfiguration";
import PocRuns from "./pages/admin/PocRuns";
import PocRunDetail from "./pages/admin/PocRunDetail";
import FlyerCaptureConsole from "./pages/admin/FlyerCaptureConsole";
// CR283: Merchant Directory
import MerchantDirectory from "./pages/admin/MerchantDirectory";
import MerchantDetail from "./pages/admin/MerchantDetail";
import MerchantSources from "./pages/admin/MerchantSources";
import MerchantCategories from "./pages/admin/MerchantCategories";
import SourceHealth from "./pages/admin/SourceHealth";
// CR070: Flyer Diagnostics v2 moved to Admin
import FlyerDiagnosticsV2 from "./pages/admin/FlyerDiagnosticsV2";
// CR072: Forum Deals moved to Admin
import ForumDeals from "./pages/admin/ForumDeals";
// CR075: Batch Runs Orchestrator
import BatchRuns from "./pages/admin/BatchRuns";
import BatchRunDetail from "./pages/admin/BatchRunDetail";
import ExecutiveSummaryDashboard from "./pages/admin/ExecutiveSummaryDashboard";
// CR095: Command Center (legacy - visibility controls)
import CommandCenter from "./pages/admin/CommandCenter";
// CR401: Deal Cards Page (new IA)
import DealCardsPage from "./pages/admin/DealCardsPage";
import TemplateEditorPage from "./pages/admin/TemplateEditorPage";
// CR097: Admin Live Preview
import LivePreview from "./pages/admin/LivePreview";
// CR107: Layout Studio
import LayoutStudio from "./pages/admin/LayoutStudio";
// CR118: Theme Labs standalone page
import ThemeLabs from "./pages/admin/ThemeLabs";
// CR113: Scheduler placeholder
import Scheduler from "./pages/admin/Scheduler";
// CR155: Schedule Run Combined Review
import ScheduleRunReview from "./pages/admin/ScheduleRunReview";
// CR114: Asset Studio
import AssetStudio from "./pages/admin/AssetStudio";
// CR136: Production Tools
import ProductionTools from "./pages/admin/ProductionTools";
// CR141: Forum Tools
import ForumTools from "./pages/admin/ForumTools";
// CR151: Deal Policy (Rules)
import DealPolicy from "./pages/admin/DealPolicy";
import PipelineTolerances from "./pages/admin/PipelineTolerances";
// CR168: Flyer Bucket Sources
import FlyerBucketSources from "./pages/admin/FlyerBucketSources";
// CR221: Builder Dashboard
import BuilderDashboard from "./pages/admin/BuilderDashboard";
// CR313: Feature Flags
import FeatureFlags from "./pages/admin/FeatureFlags";
// CR305: Recipes Studio
import RecipesStudio from "./pages/admin/RecipesStudio";
// CR408: Gate Patterns Admin
import GatePatterns from "./pages/admin/GatePatterns";
// CR485: Flyer Snippet Artifacts Viewer
import FlyerSnippetArtifacts from "./pages/admin/FlyerSnippetArtifacts";
// CR588: Merchant Anchor Intelligence
import MerchantAnchorIntelligence from "./pages/admin/MerchantAnchorIntelligence";
// Phase D: Issuer Needs-Split Review
import IssuerNeedsSplit from "./pages/admin/IssuerNeedsSplit";
// CR456: Image Capture Studio
import ImageCaptureStudio from "./pages/admin/ImageCaptureStudio";
// CR551: Storage Manager
import StorageManager from "./pages/admin/StorageManager";
// CR439: Debug route
import FlyerHydrationProof from "./pages/__debug/FlyerHydrationProof";
// CR446: Flyer snippet proof route
import FlyerSnippetProof from "./pages/__debug/FlyerSnippetProof";
// CR449: E2E flyer snippet regression proof
import FlyerSnippetE2EProof from "./pages/__debug/FlyerSnippetE2EProof";
// Figma Seed Gallery
import FigmaSeed from "./pages/FigmaSeed";
// CR130: Phase 2 - Wiring Card Size + Density to UI rendering
// CR131: Build stamp for deployment verification
const APP_CANARY = "CR131-v1";
const BUILD_TIMESTAMP = "2026-01-28 06:45"; // Force rebuild

const queryClient = new QueryClient();

const App = () => {
  // CR111C: Track mount state for canary
  const [mounted, setMounted] = useState(false);

  // CR071: Initialize merchant index on app start (stale-while-revalidate)
  useEffect(() => {
    initMerchantIndex().catch(console.error);
    setMounted(true);
    console.log(`[App] ${APP_CANARY} (built ${BUILD_TIMESTAMP}): App mounted successfully`);
  }, []);

  // CR111B/C/D: Global error listeners for debugging blank screen issues
  useEffect(() => {
    // Only add detailed logging in development
    const isDev = import.meta.env.DEV;
    
    const handleError = (event: ErrorEvent) => {
      // CR111D: Include last preview action in error log
      const lastPreviewAction = (window as unknown as { __lastPreviewAction?: string }).__lastPreviewAction;
      
      console.error(`[App] ${APP_CANARY} Global error caught:`, event.error);
      console.error(`[App] Error message:`, event.message);
      console.error(`[App] Error location:`, event.filename, "line", event.lineno, "col", event.colno);
      if (lastPreviewAction) {
        console.error(`[App] Last preview action:`, lastPreviewAction);
      }
      if (isDev && event.error?.stack) {
        console.error(`[App] Stack trace:`, event.error.stack);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // CR111D: Include last preview action in error log
      const lastPreviewAction = (window as unknown as { __lastPreviewAction?: string }).__lastPreviewAction;
      
      console.error(`[App] ${APP_CANARY} Unhandled promise rejection:`, event.reason);
      if (lastPreviewAction) {
        console.error(`[App] Last preview action:`, lastPreviewAction);
      }
      if (isDev && event.reason?.stack) {
        console.error(`[App] Promise rejection stack:`, event.reason.stack);
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    // Log that listeners are active
    console.log(`[App] ${APP_CANARY}: Global error listeners registered`);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return (
  <RootErrorBoundary fallbackTitle="Application Error">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* CR106: Global theme provider for design tokens */}
        <GlobalThemeProvider>
          {/* CR098: Single config provider for deal type registry + card templates */}
          <DealCardConfigProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              {/* CR111C: Canary badge - ONLY in development, never in production */}
              {mounted && import.meta.env.DEV && (
                <div className="fixed bottom-1 right-1 text-[10px] text-muted-foreground/30 font-mono pointer-events-none z-50">
                  App:{APP_CANARY}
                </div>
              )}
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Index />} />
                  {/* CR001: Spend & Earn route */}
                  <Route path="/spend-earn" element={<SpendEarn />} />
                  {/* CR042: Admin routes - wrapped in RootErrorBoundary for admin-specific errors */}
                  <Route path="/admin/prompt-packs" element={<RootErrorBoundary fallbackTitle="Admin Error"><PromptPacks /></RootErrorBoundary>} />
                  <Route path="/admin/prompts-library" element={<RootErrorBoundary fallbackTitle="Admin Error"><PromptsLibrary /></RootErrorBoundary>} />
                  <Route path="/admin/ai-providers" element={<RootErrorBoundary fallbackTitle="Admin Error"><AIProviders /></RootErrorBoundary>} />
                  <Route path="/admin/scan-configuration" element={<RootErrorBoundary fallbackTitle="Admin Error"><ScanConfiguration /></RootErrorBoundary>} />
                  <Route path="/admin/poc-runs" element={<RootErrorBoundary fallbackTitle="Admin Error"><PocRuns /></RootErrorBoundary>} />
                  <Route path="/admin/poc-runs/:runId" element={<RootErrorBoundary fallbackTitle="Admin Error"><PocRunDetail /></RootErrorBoundary>} />
                  <Route path="/admin/flyer-capture-console" element={<RootErrorBoundary fallbackTitle="Admin Error"><FlyerCaptureConsole /></RootErrorBoundary>} />
                  {/* CR283: Merchant Directory + Details + CR403: Categories */}
                  <Route path="/admin/merchants/directory" element={<RootErrorBoundary fallbackTitle="Admin Error"><MerchantDirectory /></RootErrorBoundary>} />
                  <Route path="/admin/merchants/categories" element={<RootErrorBoundary fallbackTitle="Admin Error"><MerchantCategories /></RootErrorBoundary>} />
                  <Route path="/admin/merchants/:merchantKey" element={<RootErrorBoundary fallbackTitle="Admin Error"><MerchantDetail /></RootErrorBoundary>} />
                  <Route path="/admin/merchant-sources" element={<RootErrorBoundary fallbackTitle="Admin Error"><MerchantSources /></RootErrorBoundary>} />
                  <Route path="/admin/source-health" element={<RootErrorBoundary fallbackTitle="Admin Error"><SourceHealth /></RootErrorBoundary>} />
                  {/* CR070: Flyer Diagnostics v2 under Admin */}
                  <Route path="/admin/flyer-diagnostics-v2" element={<RootErrorBoundary fallbackTitle="Admin Error"><FlyerDiagnosticsV2 /></RootErrorBoundary>} />
                  {/* CR072: Forum Deals under Admin */}
                  <Route path="/admin/forum-deals" element={<RootErrorBoundary fallbackTitle="Admin Error"><ForumDeals /></RootErrorBoundary>} />
                  {/* CR075: Batch Runs Orchestrator */}
                  <Route path="/admin/batch-runs" element={<RootErrorBoundary fallbackTitle="Admin Error"><BatchRuns /></RootErrorBoundary>} />
                  <Route path="/admin/dashboards/executive-summary" element={<RootErrorBoundary fallbackTitle="Admin Error"><ExecutiveSummaryDashboard /></RootErrorBoundary>} />
                  {/* CR095: Command Center (legacy visibility controls) */}
                  <Route path="/admin/command-center" element={<RootErrorBoundary fallbackTitle="Admin Error"><CommandCenter /></RootErrorBoundary>} />
                  {/* CR401: Deal Cards Page (new IA with Templates + Deal Types tabs) */}
                  <Route path="/admin/deal-cards" element={<RootErrorBoundary fallbackTitle="Admin Error"><DealCardsPage /></RootErrorBoundary>} />
                  {/* CR401: Template Editor (standalone route) */}
                  <Route path="/admin/deal-cards/templates/:deal_type_key/:variant_key" element={<RootErrorBoundary fallbackTitle="Admin Error"><TemplateEditorPage /></RootErrorBoundary>} />
                  {/* CR097: Admin Live Preview */}
                  <Route path="/admin/live-preview" element={<RootErrorBoundary fallbackTitle="Admin Error"><LivePreview /></RootErrorBoundary>} />
                  {/* CR107: Layout Studio */}
                  <Route path="/admin/layout-studio" element={<RootErrorBoundary fallbackTitle="Admin Error"><LayoutStudio /></RootErrorBoundary>} />
                  {/* CR118: Theme Labs standalone page */}
                  <Route path="/admin/theme-labs" element={<RootErrorBoundary fallbackTitle="Admin Error"><ThemeLabs /></RootErrorBoundary>} />
                  {/* CR113: Scheduler */}
                  <Route path="/admin/scheduler" element={<RootErrorBoundary fallbackTitle="Admin Error"><Scheduler /></RootErrorBoundary>} />
                  {/* CR155: Schedule Run Combined Review */}
                  <Route path="/admin/scheduler/runs/:scheduleRunId" element={<RootErrorBoundary fallbackTitle="Admin Error"><ScheduleRunReview /></RootErrorBoundary>} />
                  {/* CR114: Asset Studio */}
                  <Route path="/admin/asset-studio" element={<RootErrorBoundary fallbackTitle="Admin Error"><AssetStudio /></RootErrorBoundary>} />
                  {/* CR221: Builder Dashboard */}
                  <Route path="/admin/builder" element={<RootErrorBoundary fallbackTitle="Admin Error"><BuilderDashboard /></RootErrorBoundary>} />
                  {/* CR136: Production Tools */}
                  <Route path="/admin/production-tools" element={<RootErrorBoundary fallbackTitle="Admin Error"><ProductionTools /></RootErrorBoundary>} />
                  {/* CR313: Feature Flags */}
                  <Route path="/admin/feature-flags" element={<RootErrorBoundary fallbackTitle="Admin Error"><FeatureFlags /></RootErrorBoundary>} />
                  {/* CR141: Forum Tools */}
                  <Route path="/admin/forum-tools" element={<RootErrorBoundary fallbackTitle="Admin Error"><ForumTools /></RootErrorBoundary>} />
                  {/* CR151: Deal Policy (Rules) */}
                  <Route path="/admin/deal-policy" element={<RootErrorBoundary fallbackTitle="Admin Error"><DealPolicy /></RootErrorBoundary>} />
                  {/* CR730: Pipeline Tolerances */}
                  <Route path="/admin/pipeline-tolerances" element={<RootErrorBoundary fallbackTitle="Admin Error"><PipelineTolerances /></RootErrorBoundary>} />
                  {/* CR168: Flyer Bucket Sources */}
                  <Route path="/admin/flyer-bucket-sources" element={<RootErrorBoundary fallbackTitle="Admin Error"><FlyerBucketSources /></RootErrorBoundary>} />
                  {/* CR485: Flyer Snippet Artifacts Viewer */}
                  <Route path="/admin/flyer-snippets" element={<RootErrorBoundary fallbackTitle="Admin Error"><FlyerSnippetArtifacts /></RootErrorBoundary>} />
                  <Route path="/admin/batch-runs/:runId" element={<RootErrorBoundary fallbackTitle="Batch Run Error"><BatchRunDetail /></RootErrorBoundary>} />
                  {/* CR305: Recipes Studio */}
                  <Route path="/admin/recipes" element={<RootErrorBoundary fallbackTitle="Admin Error"><RecipesStudio /></RootErrorBoundary>} />
                  {/* CR408: Gate Patterns */}
                  <Route path="/admin/gate-patterns" element={<RootErrorBoundary fallbackTitle="Admin Error"><GatePatterns /></RootErrorBoundary>} />
                  {/* Phase D: Issuer Needs-Split Review */}
                  <Route path="/admin/merchants/issuer-needs-split" element={<RootErrorBoundary fallbackTitle="Admin Error"><IssuerNeedsSplit /></RootErrorBoundary>} />
                  {/* CR456: Image Capture Studio */}
                  <Route path="/admin/image-capture" element={<RootErrorBoundary fallbackTitle="Admin Error"><ImageCaptureStudio /></RootErrorBoundary>} />
                  {/* CR551: Storage Manager */}
                  <Route path="/admin/storage-manager" element={<RootErrorBoundary fallbackTitle="Admin Error"><StorageManager /></RootErrorBoundary>} />
                  {/* CR588: Merchant Anchor Intelligence */}
                  <Route path="/admin/anchor-intelligence" element={<RootErrorBoundary fallbackTitle="Admin Error"><MerchantAnchorIntelligence /></RootErrorBoundary>} />
                  {/* CR439: Dev-only flyer hydration proof harness */}
                  {(import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG_ROUTES === 'true') && (<>
                    <Route path="/__debug/flyer-hydration" element={<FlyerHydrationProof />} />
                    <Route path="/__debug/flyer-snippet-proof" element={<FlyerSnippetProof />} />
                    <Route path="/__debug/flyer-snippet-e2e" element={<FlyerSnippetE2EProof />} />
                  </>)}
                  {/* Figma Seed Gallery */}
                  <Route path="/figma-seed" element={<FigmaSeed />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </DealCardConfigProvider>
        </GlobalThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </RootErrorBoundary>
  );
};

export default App;
